import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ProductDevelopmentActions from './ProductDevelopmentActions';
import { useStyles } from './GenericTableStyles';
import Pagination from '../../logisticsDashboard/components/Pagination';

const GenericTable = ({
  data = [],
  columns = [],
  viewType = "overallView",
  actions = [],
  featuresAccess = {},
  onActionClick = null,
  showActionsColumn = false,
  pageSize = 10
}) => {
  const classes = useStyles();
  const PAGE_SIZE = pageSize || 10;

  const visibleColumns = React.useMemo(() => {
    return columns || [];
  }, [columns]);

  const getFieldName = (columnName) => {
    return columnName
      .split(' ')
      .map((word, index) => {
        if (index === 0) {
          return word.charAt(0).toLowerCase() + word.slice(1);
        }
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join('')
      .replace(/[^a-zA-Z0-9]/g, '');
  };

  const getColumnDisplayName = (column) => {
    let name = column.name || '';
    if (name.toUpperCase() === 'UOM' || name === 'U.O.M') {
      name = 'UoM';
    }
    // "Procedure Number" is presented as "Phase / Step Number" across all tabs.
    if (name.trim().toLowerCase() === 'procedure number') {
      name = 'Phase / Step Number';
    }
    name = name.replace(/\s+ID\b/gi, '\u00A0I\u2060D');
    name = name.replace(/\bID\b/gi, 'I\u2060D');
    return name;
  };

  const getColumnFieldName = (column) => {
    if (column.field) {
      return column.field;
    }
    return getFieldName(column.name);
  };

  const getCellClass = (column) => {
    const displayName = getColumnDisplayName(column);
    const lowerName = displayName.toLowerCase();
    if (lowerName === "uom" || lowerName === "u.o.m") {
      return classes.uomCell;
    }
    return classes.tableCell;
  };

  const totalCols = visibleColumns.length + (showActionsColumn && actions && actions.length > 0 ? 1 : 0);

  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil((data ? data.length : 0) / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageData = useMemo(
    () => (data || []).slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [data, safePage, PAGE_SIZE]
  );

  if (!data || data.length === 0) {
    return (
      <Paper className={classes.paper} style={{ marginTop: '10px' }}>
        <div className={classes.tableWrapper}>
          <Table className={classes.table} stickyHeader>
            <TableHead>
              <TableRow>
                {visibleColumns.map((column) => {
                  const displayName = getColumnDisplayName(column);
                  return (
                    <TableCell
                      key={column.key || column.id || column.name || column.displayName}
                      align="left"
                      className={classes.headerCell}
                    >
                      {displayName}
                    </TableCell>
                  );
                })}
                {showActionsColumn && actions && actions.length > 0 && (
                  <TableCell align="center" className={classes.headerCell}>
                    Actions
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell colSpan={totalCols} align="center" className={classes.tableCell}>
                  No data available
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </Paper>
    );
  }

  return (
    <Paper className={classes.paper} style={{ marginTop: '10px' }}>
      <div className={classes.tableWrapper}>
        <Table className={classes.table} stickyHeader>
          <TableHead>
            <TableRow>
              {visibleColumns.map((column) => {
                const displayName = getColumnDisplayName(column);
                return (
                  <TableCell
                    key={column.key || column.id || column.name || column.displayName}
                    align="left"
                    className={classes.headerCell}
                  >
                    {displayName}
                  </TableCell>
                );
              })}
              {showActionsColumn && actions && actions.length > 0 && (
                <TableCell align="center" className={classes.headerCell}>
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {pageData.map((row, index) => (
              <TableRow key={index} className={classes.tableRow}>
                {visibleColumns.map((column) => {
                  const fieldName = getColumnFieldName(column);
                  const cellValue = row[fieldName] !== undefined && row[fieldName] !== null
                    ? row[fieldName]
                    : '-';

                  const cellValueStr = String(cellValue);
                  const isPdf = cellValueStr.trim().toLowerCase().endsWith('.pdf') ||
                               cellValueStr.trim().toLowerCase().includes('.pdf');

                  return (
                    <TableCell
                      key={column.key || column.id || column.name || column.displayName}
                      align="left"
                      className={getCellClass(column)}
                    >
                      {isPdf ? (
                        <span style={{ color: '#1976d2', cursor: 'pointer' }}>
                          {cellValue}
                        </span>
                      ) : (
                        cellValue
                      )}
                    </TableCell>
                  );
                })}
                {showActionsColumn && actions && actions.length > 0 && (
                  <TableCell align="center" className={classes.tableCell}>
                    <ProductDevelopmentActions
                      row={row}
                      actions={actions}
                      featuresAccess={featuresAccess}
                      onActionClick={onActionClick}
                    />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {data.length > PAGE_SIZE && (
        <Box style={{ padding: '4px 0 12px' }}>
          <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
        </Box>
      )}
    </Paper>
  );
};

export default GenericTable;
